import React, { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Floating formatting toolbar for LP section inline text editing.
 * Appears whenever text is selected inside an element with [data-lp-editable].
 */
const LPInlineFloatingToolbar: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [showColors, setShowColors] = useState(false);
  const savedRangeRef = useRef<Range | null>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);

  const isInsideEditableLP = (node: Node | null): boolean => {
    if (!node) return false;
    let el: Element | null = node.nodeType === Node.TEXT_NODE ? node.parentElement : (node as Element);
    while (el) {
      if (el.hasAttribute('data-lp-editable')) return true;
      el = el.parentElement;
    }
    return false;
  };

  const updatePosition = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
      setVisible(false);
      return;
    }
    if (!isInsideEditableLP(sel.anchorNode)) {
      setVisible(false);
      return;
    }
    const range = sel.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    if (rect.width === 0) { setVisible(false); return; }

    setPos({
      top: rect.top + window.scrollY - 52,
      left: rect.left + window.scrollX + rect.width / 2,
    });
    setVisible(true);
  }, []);

  useEffect(() => {
    document.addEventListener('selectionchange', updatePosition);
    return () => document.removeEventListener('selectionchange', updatePosition);
  }, [updatePosition]);

  // Hide toolbar when clicking outside
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (toolbarRef.current && !toolbarRef.current.contains(e.target as Node)) {
        setShowColors(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, []);

  const saveRange = () => {
    const sel = window.getSelection();
    if (sel && !sel.isCollapsed && sel.rangeCount > 0) {
      savedRangeRef.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const restoreRange = () => {
    if (!savedRangeRef.current) return;
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(savedRangeRef.current);
  };

  const exec = (cmd: string, value?: string) => {
    restoreRange();
    document.execCommand(cmd, false, value ?? undefined);
    // Dispatch input event so the editable element's onInput fires (if wired)
    const sel = window.getSelection();
    if (sel?.anchorNode) {
      let el: Element | null = sel.anchorNode.parentElement;
      while (el && !el.hasAttribute('data-lp-editable')) el = el.parentElement;
      if (el) el.dispatchEvent(new Event('input', { bubbles: true }));
    }
  };

  const applyColor = (color: string) => {
    exec('foreColor', color);
    setShowColors(false);
  };

  const applyFontSize = (px: string) => {
    restoreRange();
    // Use execCommand('fontSize', 7) as a placeholder, then replace the generated
    // <font size="7"> element with <span style="font-size: Xpx">
    document.execCommand('fontSize', false, '7');
    const sel = window.getSelection();
    if (sel?.anchorNode) {
      let root: Element | null = sel.anchorNode.nodeType === Node.TEXT_NODE
        ? sel.anchorNode.parentElement
        : sel.anchorNode as Element;
      while (root && !root.hasAttribute('data-lp-editable')) root = root.parentElement;
      if (root) {
        root.querySelectorAll('font[size="7"]').forEach((font) => {
          const span = document.createElement('span');
          span.style.fontSize = px;
          while (font.firstChild) span.appendChild(font.firstChild);
          font.parentNode?.replaceChild(span, font);
        });
        root.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  };

  const FONT_SIZES = [
    { label: '12', value: '12px' },
    { label: '14', value: '14px' },
    { label: '16', value: '16px' },
    { label: '18', value: '18px' },
    { label: '20', value: '20px' },
    { label: '24', value: '24px' },
    { label: '28', value: '28px' },
    { label: '32', value: '32px' },
    { label: '36', value: '36px' },
    { label: '48', value: '48px' },
  ];

  const COLOR_PRESETS = [
    '#0f172a', '#1e3a5f', '#2177FF', '#1EACA6', '#7c3aed',
    '#dc2626', '#ea580c', '#ca8a04', '#16a34a', '#0891b2',
    '#ffffff', '#f1f5f9', '#94a3b8', '#64748b', '#475569',
    '#fef3c7', '#fce7f3', '#ede9fe', '#d1fae5', '#dbeafe',
  ];

  if (!visible) return null;

  return (
    <div
      ref={toolbarRef}
      style={{
        position: 'absolute',
        top: pos.top,
        left: pos.left,
        transform: 'translateX(-50%)',
        zIndex: 9999,
        background: 'rgba(15,23,42,0.93)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        borderRadius: '8px',
        padding: '5px 8px',
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.35)',
        userSelect: 'none',
      }}
      onMouseDown={(e) => { e.preventDefault(); saveRange(); }}
    >
      {/* Bold */}
      <button
        title="Bold"
        onClick={() => exec('bold')}
        style={btnStyle}
      >
        <strong>B</strong>
      </button>

      {/* Italic */}
      <button
        title="Italic"
        onClick={() => exec('italic')}
        style={btnStyle}
      >
        <em>I</em>
      </button>

      {/* Underline */}
      <button
        title="Underline"
        onClick={() => exec('underline')}
        style={btnStyle}
      >
        <span style={{ textDecoration: 'underline' }}>U</span>
      </button>

      {/* Divider */}
      <div style={{ width: '1px', height: '16px', background: 'rgba(255,255,255,0.2)', margin: '0 3px' }} />

      {/* Font size */}
      <select
        title="Font size"
        defaultValue=""
        onChange={(e) => { if (e.target.value) { applyFontSize(e.target.value); e.target.value = ''; } }}
        onMouseDown={(e) => { e.stopPropagation(); saveRange(); }}
        style={{
          background: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.15)',
          borderRadius: '4px',
          color: '#ffffff',
          fontSize: '0.75rem',
          padding: '2px 2px',
          cursor: 'pointer',
          outline: 'none',
          height: '26px',
          minWidth: '46px',
        }}
      >
        <option value="" disabled style={{ background: '#1e293b' }}>Size</option>
        {FONT_SIZES.map((s) => (
          <option key={s.value} value={s.value} style={{ background: '#1e293b' }}>{s.label}</option>
        ))}
      </select>

      {/* Divider */}
      <div style={{ width: '1px', height: '16px', background: 'rgba(255,255,255,0.2)', margin: '0 3px' }} />

      {/* Text colour */}
      <div style={{ position: 'relative' }}>
        <button
          title="Text colour"
          onClick={() => { saveRange(); setShowColors(v => !v); }}
          style={btnStyle}
        >
          <span style={{ fontWeight: 700, letterSpacing: '-0.05em' }}>A</span>
          <span style={{
            display: 'block', width: '14px', height: '3px',
            background: 'linear-gradient(90deg,#f00,#ff0,#0f0,#0ff,#00f)',
            borderRadius: '1px', marginTop: '1px',
          }} />
        </button>

        {showColors && (
          <div style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(15,23,42,0.96)',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            borderRadius: '10px',
            padding: '10px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            minWidth: '174px',
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: '5px' }}>
              {COLOR_PRESETS.map((c) => (
                <div
                  key={c}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => applyColor(c)}
                  title={c}
                  style={{
                    width: '24px', height: '24px', borderRadius: '5px',
                    background: c, cursor: 'pointer',
                    border: c === '#ffffff' ? '1px solid rgba(255,255,255,0.25)' : 'none',
                    transition: 'transform 0.1s',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.2)')}
                  onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
                />
              ))}
            </div>
            {/* Custom hex input */}
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <input
                type="color"
                defaultValue="#000000"
                onChange={(e) => applyColor(e.target.value)}
                title="Custom colour"
                style={{ width: '28px', height: '24px', border: 'none', borderRadius: '4px', cursor: 'pointer', padding: 0, flexShrink: 0 }}
              />
              <input
                type="text"
                placeholder="#000000"
                maxLength={7}
                style={{
                  flex: 1, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '4px', color: '#fff', fontSize: '0.7rem',
                  padding: '3px 6px', fontFamily: 'monospace', outline: 'none',
                }}
                onChange={(e) => {
                  const v = e.target.value;
                  if (/^#[0-9A-Fa-f]{6}$/.test(v)) applyColor(v);
                }}
              />
            </div>
            {/* Reset */}
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { exec('removeFormat'); setShowColors(false); }}
              style={{
                background: 'rgba(255,255,255,0.08)', border: 'none', color: '#94a3b8',
                borderRadius: '4px', fontSize: '0.7rem', padding: '4px', cursor: 'pointer',
              }}
            >
              Remove formatting
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const btnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#ffffff',
  cursor: 'pointer',
  padding: '4px 8px',
  borderRadius: '4px',
  fontSize: '0.85rem',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  lineHeight: 1.2,
  transition: 'background 0.1s',
};

export default LPInlineFloatingToolbar;
